<template>
  <div class="metric-statistics">
    <div class="info-btn-wrapper">
      <b-btn :id="`metricInfoPopover_${metric}`" :class="'popover-btn'">
        <img class="static" src="@/assets/images/common/info-btn.png" alt="info btn">
        <img class="hover" src="@/assets/images/common/info-btn-hover.png" alt="info btn">
      </b-btn>

      <div class="dashboard-popover custom-popover" :id="`popoverPlacement_MetricInfoPopover_${metric}`"></div>

      <b-popover 
        :target="`metricInfoPopover_${metric}`" 
        triggers="hover" 
        placement="top"
        :container="`popoverPlacement_MetricInfoPopover_${metric}`">
        <MetricPopoverContent 
          v-if="sessionMetrics.TIME_ABOVE_80_PERCENT_HR === metric"
          :metric="metric" 
          :metric-label="$t('DASHBOARD.metricPopover.labels.timeOver80')"
          :zone1-label="'0 - 15 min'"
          :zone2-label="'15 - 30 min'"
          :zone3-label="'> 30 min'" />

        <MetricPopoverContent 
          v-if="sessionMetrics.AP === metric"
          :metric="metric" 
          :metric-label="$t('DASHBOARD.metricPopover.labels.ap')"
          :zone1-label="'0 - 40%'"
          :zone2-label="'40-60%'"
          :zone3-label="'60-80%'" />

        <MetricPopoverContent 
          v-if="sessionMetrics.DISTANCE === metric"
          :metric="metric" 
          :metric-label="$t('DASHBOARD.metricPopover.labels.distance')"
          :zone1-label="zone1Label"
          :zone2-label="zone2Label"
          :zone3-label="zone3Label" />

        <MetricPopoverContent 
          v-if="sessionMetrics.WORKLOAD === metric"
          :metric="metric" 
          :metric-label="$t('DASHBOARD.metricPopover.labels.workload')"
          :zone1-label="'0 - 10'"
          :zone2-label="'10 - 20'"
          :zone3-label="'> 20'" />
      </b-popover>

    </div>  
    <div class="metric-cell-wrapper">
      <div class="metric-cell">
        <div class="icon-wrapper">
          <img v-if="sessionMetrics.TIME_ABOVE_80_PERCENT_HR === metric" src="@/assets/images/activity/hr-icon.png" alt="heart rate icon">
          <img v-if="sessionMetrics.AP === metric" src="@/assets/images/activity/active-participation-icon.png" alt="ap icon">
          <img v-if="sessionMetrics.DISTANCE === metric" src="@/assets/images/activity/distance-speed-icon.png" alt="distance icon">
          <img v-if="sessionMetrics.WORKLOAD === metric" src="@/assets/images/activity/workload-icon.png" alt="workload icon">
        </div>
        <h5 class="text-gray metric-title">
          {{title}}
        </h5>
        <h3 class="metric-value">
          <template v-if="value !== null">{{formattedValue}} {{unit}}</template>
          <template v-if="value === null">{{ $t('COMMON.notApplicable')}}</template>
        </h3>
        <div class="button-wrapper">
          <Button
            :styleType="btnStyleType"
            @click="changeSelectedMetricAndLog(metric)"
            :class="{active: currentMetric === metric}">
            {{ $t('DASHBOARD.donutCharts.showTimeline')}}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import numeral from 'numeral'
import BtnTypes from '@/constants/BtnTypes'
import Button from '@/components/common/Button'
import MetricPopoverContent from '@/components/calendar-activity/MetricPopoverContent'
import { convertDistance } from '@/common/helper'
import { UNIT_SYSTEM } from '../../constants/UnitConsts'

export default {
  name: 'MetricInfo',
  props: [
    'metric',
    'value',
    'currentMetric',
    'changeSelectedMetric',
    'sessionMetrics',
    'currentPage',
    'account'
  ],
  data () {
    return {
      btnTypes: BtnTypes
    }
  },
  computed: {
    title () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          return this.$t('DASHBOARD.donutCharts.timeOver80');
        case this.sessionMetrics.AP:
          return this.$t('DASHBOARD.donutCharts.activeParticipation');
        case this.sessionMetrics.DISTANCE:
          return this.$t('DASHBOARD.donutCharts.distance');
        case this.sessionMetrics.WORKLOAD:
          return this.$t('DASHBOARD.donutCharts.workload');
      }
    },
    btnStyleType () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          return this.btnTypes.RED_BORDER_WHITE_TEXT
        case this.sessionMetrics.AP:
          return this.btnTypes.YELLOW_BORDER_WHITE_TEXT
        case this.sessionMetrics.DISTANCE:
          return this.btnTypes.BLUE_BORDER_WHITE_TEXT
        case this.sessionMetrics.WORKLOAD:
          return this.btnTypes.GREEN_BORDER_WHITE_TEXT
      }
    },
    unit () {
      switch(this.metric) {
        case this.sessionMetrics.TIME_ABOVE_80_PERCENT_HR:
          return ' min'
        case this.sessionMetrics.AP:
          return '%'
        case this.sessionMetrics.DISTANCE:
          return ''
        case this.sessionMetrics.WORKLOAD:
          return ''
      }
    },
    formattedValue () {
      if (this.metric === this.sessionMetrics.DISTANCE) {
        return this.$options.filters.distance(this.value, this.account.unitDistance);
      }
      return this.value
    },
    zone1Label () {
      return `0 - ${this.$options.filters.distance(2500, this.account.unitDistance)}`
    }, 
    zone2Label () {
      return `${numeral(convertDistance(2500, this.account.unitDistance)).format(this.account.unitDistance === UNIT_SYSTEM.IMPERIAL ? '0,0.00' : '0,0')} - ${this.$options.filters.distance(5000, this.account.unitDistance)}`
    }, 
    zone3Label () {
      return `> ${this.$options.filters.distance(5000, this.account.unitDistance)}`
    } 
  },
  methods: {
    changeSelectedMetricAndLog(metric) {
      this.$amplitude.logEvent(`pwa: change ${this.currentPage} timeline`, {timelineType: metric})
      this.changeSelectedMetric(metric)
    }
  },
  components: {
    Button,
    MetricPopoverContent
  }
}
</script>

<style lang="scss" scoped>
  .metric-statistics {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;

    .metric-cell-wrapper {
      display: table;
      width: 100%;
      height: 100%;

      .metric-cell {
        display: table-cell;
        vertical-align: middle;
        text-align: center;

        .metric-title {
          font-size: 14px;
          margin-top: 10px;
          margin-bottom: 10px;
        }

        .metric-value {
          font-size: 24px;
          margin-top: 20px;
          margin-bottom: 10px;
        }
      }
    }
  }

  .info-btn-wrapper {
    position: absolute;
    right: 20px;
    top: 0;

    .static {
      display: inline-block;
    }

    .hover {
      display: none;
    }

    &:hover {
      .static {
        display: none;
      }

      .hover {
        display: inline-block;
      }
    }
  }
</style>
